/* ==========================================================================
   Solid Viibe — AJAX predictive search
   Queries Shopify's Predictive Search API as the customer types and renders
   live results into the dropdown markup from snippets/predictive-search.liquid.
   Vanilla, no framework, matches the shape of assets/cart.js.

   The panel's <form action="/search" method="get"> is real and untouched —
   with JavaScript off, or if this file fails to load, the search icon still
   links straight to the search page and the form still submits there. This
   file only intercepts things to make it faster when it can.

   Public surface:
     SolidViibe.search.open()  / close()
   ========================================================================== */
(function () {
  'use strict';

  var root = window.SolidViibe = window.SolidViibe || {};
  var routes = (window.Shopify && window.Shopify.routes) || {};
  var ROOT = routes.root || '/';

  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 250;
  var RESULT_LIMIT = 5;

  /* product.js falls back the same way when global.js has not run yet. */
  var money = (root.formatMoney) || function (cents) {
    return '$' + (Math.round(cents) / 100).toFixed(2);
  };

  function url(path) {
    return (ROOT + path).replace(/\/{2,}/g, '/');
  }

  /* --- Escaping ----------------------------------------------------------
     Results come back as JSON and are inserted with innerHTML, so anything
     that could contain markup (titles, in particular) is escaped by hand —
     there is no templating layer here to do it for us. */
  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* Predictive Search returns product price as a decimal-dollar string
     (e.g. "24.00"), not cents like the Cart API. Converting it up front
     means formatMoney(cents) stays the theme's one money formatter instead
     of growing a second cents-vs-dollars code path. */
  function priceCents(raw) {
    var value = parseFloat(raw);
    if (isNaN(value)) return 0;
    return Math.round(value * 100);
  }

  /* Shopify CDN images accept a width param; predictive search hands back
     an un-sized URL, so a thumbnail-appropriate size is appended by hand. */
  function resizeImage(src, width) {
    if (!src) return src;
    var sep = src.indexOf('?') === -1 ? '?' : '&';
    return src + sep + 'width=' + width;
  }

  function searchPageUrl(query) {
    return url('search') + '?q=' + encodeURIComponent(query) + '&type=product,page,article';
  }

  /* --- Elements ------------------------------------------------------- */
  function panelEl() { return document.querySelector('[data-search-panel]'); }
  function inputEl() { return document.querySelector('[data-search-input]'); }
  function resultsEl() { return document.querySelector('[data-search-results]'); }
  function announceEl() { return document.querySelector('[data-search-announce]'); }
  function viewAllEl() { return document.querySelector('[data-search-viewall]'); }

  function announce(text) {
    var el = announceEl();
    if (el) el.textContent = text;
  }

  /* --- Panel open / close, matching cart.js's setDrawer -------------- */
  var lastFocused = null;

  function setPanel(open) {
    var panel = panelEl();
    if (!panel) return;
    panel.dataset.open = String(open);
    panel.setAttribute('aria-hidden', String(!open));
    if (open) { panel.removeAttribute('inert'); } else { panel.setAttribute('inert', ''); }
    document.body.style.overflow = open ? 'hidden' : '';

    var overlay = document.querySelector('[data-search-overlay]');
    if (overlay) overlay.dataset.open = String(open);

    var openers = document.querySelectorAll('[data-search-open]');
    Array.prototype.forEach.call(openers, function (b) {
      b.setAttribute('aria-expanded', String(open));
    });

    if (open) {
      lastFocused = document.activeElement;
      reset();
      var input = inputEl();
      if (input) {
        input.value = '';
        input.focus();
      }
    } else {
      abortInFlight();
      window.clearTimeout(debounceId);
      reset();
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
      lastFocused = null;
    }
  }

  root.search = {
    open: function () { setPanel(true); },
    close: function () { setPanel(false); }
  };

  /* --- Request handling ------------------------------------------------
     One in-flight request at a time. A newer keystroke aborts whatever is
     still on the wire, and the resolved handler double-checks the query it
     answers against the box's current value, so a response that slips in
     after being aborted (older browsers with no AbortController) can never
     overwrite a newer, still-in-progress result. */
  var debounceId = null;
  var activeController = null;
  var currentQuery = '';

  function abortInFlight() {
    if (activeController && typeof activeController.abort === 'function') {
      activeController.abort();
    }
    activeController = null;
  }

  function onInput(e) {
    var input = e.target;
    var query = input.value.replace(/^\s+|\s+$/g, '');

    window.clearTimeout(debounceId);

    if (query.length < MIN_CHARS) {
      abortInFlight();
      reset();
      return;
    }

    debounceId = window.setTimeout(function () { runSearch(query); }, DEBOUNCE_MS);
  }

  function runSearch(query) {
    abortInFlight();
    currentQuery = query;
    setLoading(query);

    activeController = window.AbortController ? new AbortController() : null;
    var opts = { headers: { Accept: 'application/json' } };
    if (activeController) opts.signal = activeController.signal;

    var params = [
      'q=' + encodeURIComponent(query),
      'resources[type]=product,page,article',
      'resources[limit]=' + RESULT_LIMIT,
      'resources[options][unavailable_products]=last'
    ].join('&');

    fetch(url('search/suggest.json') + '?' + params, opts)
      .then(function (res) {
        if (!res.ok) throw new Error('Search request failed');
        return res.json();
      })
      .then(function (data) {
        if (query !== currentQuery) return; /* superseded — drop it */
        activeController = null;
        render(data, query);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (query !== currentQuery) return;
        activeController = null;
        renderError(query);
      });
  }

  /* --- Rendering -------------------------------------------------------- */
  var optionEls = [];
  var activeIndex = -1;

  function reset() {
    window.clearTimeout(debounceId);
    activeIndex = -1;
    optionEls = [];
    currentQuery = '';

    var results = resultsEl();
    if (results) {
      results.innerHTML = '';
      results.removeAttribute('role');
      results.removeAttribute('aria-busy');
    }

    var link = viewAllEl();
    if (link) link.hidden = true;

    announce('');

    var input = inputEl();
    if (input) {
      input.removeAttribute('aria-activedescendant');
      input.setAttribute('aria-expanded', 'false');
    }
  }

  function setLoading(query) {
    var results = resultsEl();
    if (!results) return;
    optionEls = [];
    activeIndex = -1;
    results.removeAttribute('role');
    results.setAttribute('aria-busy', 'true');
    results.innerHTML = '<p class="sv-psearch__loading">Searching&hellip;</p>';
    announce('Searching for ' + query + '…');
  }

  function optionHtml(id, href, thumbHtml, title, meta) {
    return (
      '<a class="sv-psearch__option" role="option" id="' + id + '" tabindex="-1"' +
      ' href="' + escapeHtml(href) + '" data-search-option data-url="' + escapeHtml(href) + '">' +
      thumbHtml +
      '<span class="sv-psearch__option-body">' +
      '<span class="sv-psearch__option-title">' + escapeHtml(title) + '</span>' +
      (meta ? '<span class="sv-psearch__option-meta">' + meta + '</span>' : '') +
      '</span></a>'
    );
  }

  function productOptionHtml(product, id) {
    var thumb;
    if (product.image) {
      thumb = '<img class="sv-psearch__thumb" src="' + escapeHtml(resizeImage(product.image, 88)) +
        '" alt="" width="44" height="44" loading="lazy">';
    } else {
      thumb = '<span class="sv-psearch__thumb sv-psearch__thumb--empty" aria-hidden="true"></span>';
    }
    var priceText = escapeHtml(money(priceCents(product.price)));
    var meta = '<span class="sv-psearch__price">' + priceText + '</span>';
    return optionHtml(id, product.url, thumb, product.title, meta);
  }

  function pageOptionHtml(item, id) {
    var thumb = '<span class="sv-psearch__thumb sv-psearch__thumb--empty" aria-hidden="true"></span>';
    return optionHtml(id, item.url, thumb, item.title, '');
  }

  function groupHtml(label, itemsHtml) {
    return (
      '<div class="sv-psearch__group" role="group" aria-label="' + escapeHtml(label) + '">' +
      '<p class="sv-psearch__group-label" aria-hidden="true">' + escapeHtml(label) + '</p>' +
      itemsHtml +
      '</div>'
    );
  }

  function render(data, query) {
    var results = resultsEl();
    if (!results) return;

    var found = (data && data.resources && data.resources.results) || {};
    var products = found.products || [];
    var pages = (found.pages || []).concat(found.articles || []);
    var total = products.length + pages.length;

    optionEls = [];
    activeIndex = -1;
    results.removeAttribute('aria-busy');

    if (total === 0) {
      results.removeAttribute('role');
      results.innerHTML =
        '<p class="sv-psearch__empty">No matches for &ldquo;' + escapeHtml(query) + '&rdquo;.</p>';
      announce('No matches for ' + query + '.');
      showViewAll(query);
      var input0 = inputEl();
      if (input0) input0.setAttribute('aria-expanded', 'false');
      return;
    }

    var html = '';
    var uid = 0;

    if (products.length) {
      var productsHtml = '';
      for (var i = 0; i < products.length; i++) {
        uid += 1;
        productsHtml += productOptionHtml(products[i], 'sv-search-option-' + uid);
      }
      html += groupHtml('Products', productsHtml);
    }

    if (pages.length) {
      var pagesHtml = '';
      for (var j = 0; j < pages.length; j++) {
        uid += 1;
        pagesHtml += pageOptionHtml(pages[j], 'sv-search-option-' + uid);
      }
      html += groupHtml('Pages &amp; articles', pagesHtml);
    }

    results.setAttribute('role', 'listbox');
    results.innerHTML = html;
    optionEls = Array.prototype.slice.call(results.querySelectorAll('[data-search-option]'));

    var input1 = inputEl();
    if (input1) input1.setAttribute('aria-expanded', 'true');

    announce(total + (total === 1 ? ' result' : ' results') + ' for ' + query + '.');
    showViewAll(query);
  }

  function renderError(query) {
    var results = resultsEl();
    if (!results) return;
    results.removeAttribute('role');
    results.removeAttribute('aria-busy');
    optionEls = [];
    activeIndex = -1;
    results.innerHTML = '<p class="sv-psearch__empty">Search is not responding. Try again, or press Enter to see full results.</p>';
    announce('Search is not responding.');
    showViewAll(query);
  }

  function showViewAll(query) {
    var link = viewAllEl();
    if (!link) return;
    link.href = searchPageUrl(query);
    var label = link.querySelector('[data-search-viewall-label]');
    if (label) label.textContent = 'See all results for “' + query + '”';
    link.hidden = false;
  }

  /* --- Keyboard: combobox roving highlight ------------------------------ */
  function highlight() {
    var input = inputEl();
    for (var i = 0; i < optionEls.length; i++) {
      var on = i === activeIndex;
      optionEls[i].classList.toggle('is-active', on);
      optionEls[i].setAttribute('aria-selected', String(on));
    }
    if (!input) return;
    if (activeIndex > -1 && optionEls[activeIndex]) {
      input.setAttribute('aria-activedescendant', optionEls[activeIndex].id);
      if (optionEls[activeIndex].scrollIntoView) optionEls[activeIndex].scrollIntoView({ block: 'nearest' });
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  }

  function moveHighlight(delta) {
    if (!optionEls.length) return;
    activeIndex += delta;
    if (activeIndex < -1) activeIndex = optionEls.length - 1;
    if (activeIndex >= optionEls.length) activeIndex = -1;
    highlight();
  }

  /* --- Wiring ------------------------------------------------------------
     Delegated on document, like cart.js — the panel's inner markup is
     rebuilt on every keystroke, so nothing here can hold a stale reference
     to it. */
  document.addEventListener('input', function (e) {
    if (!e.target.matches || !e.target.matches('[data-search-input]')) return;
    onInput(e);
  });

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-search-open]');
    if (opener) {
      e.preventDefault();
      setPanel(true);
      return;
    }

    if (e.target.closest('[data-search-close]') || e.target.closest('[data-search-overlay]')) {
      e.preventDefault();
      setPanel(false);
      return;
    }

    if (e.target.closest('[data-search-clear]')) {
      e.preventDefault();
      var input = inputEl();
      if (input) {
        input.value = '';
        input.focus();
      }
      abortInFlight();
      reset();
      return;
    }

    /* Click outside the panel and its opener closes it. */
    var panel = panelEl();
    if (panel && panel.dataset.open === 'true' &&
        !e.target.closest('[data-search-panel]') && !e.target.closest('[data-search-open]')) {
      setPanel(false);
    }
  });

  document.addEventListener('keydown', function (e) {
    var panel = panelEl();
    if (!panel || panel.dataset.open !== 'true') return;

    if (e.key === 'Escape') {
      e.preventDefault();
      setPanel(false);
      return;
    }

    var input = inputEl();
    var onInputField = input && document.activeElement === input;

    if (onInputField && e.key === 'ArrowDown') {
      e.preventDefault();
      moveHighlight(1);
      return;
    }
    if (onInputField && e.key === 'ArrowUp') {
      e.preventDefault();
      moveHighlight(-1);
      return;
    }
    if (onInputField && e.key === 'Enter' && activeIndex > -1 && optionEls[activeIndex]) {
      /* An option is highlighted only virtually (aria-activedescendant) —
         real focus never leaves the input — so Enter has to navigate by
         hand. With nothing highlighted this branch is skipped and the real
         <form> submits to the search page as normal. */
      e.preventDefault();
      window.location.href = optionEls[activeIndex].getAttribute('data-url');
      return;
    }

    /* Focus trap while the panel is open — mirrors header.js's drawer trap.
       The result rows carry tabindex="-1" on purpose (they are reached by
       arrow keys, not Tab), so they are excluded here. */
    if (e.key === 'Tab') {
      var focusables = panel.querySelectorAll(
        'input:not([disabled]), button:not([disabled]), a[href]:not([data-search-option])'
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });
})();
